import { useEffect, useRef } from 'react';

import { isEqual } from 'lodash-es';
import { parse, stringify } from 'qs';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserHistory as History, Location } from 'react-resource-router';

import {
  changeFilterState,
  saveFilterState,
  fetchJiraTabFiltersUserPreference,
  setJiraTabFiltersUserPreferenceNotRequired,
} from 'src/redux/jira/actions';
import { IssueCategory } from 'src/redux/jira/constants';
import { emptyFilterState } from 'src/redux/jira/reducers/jira-repo-page';
import { FilterState, Sort, SORT_ORDER } from 'src/redux/jira/types';
import { BucketState } from 'src/types/state';

enum QueryParam {
  Site = 'site',
  Projects = 'projects',
  Statuses = 'statuses',
  Search = 'search',
  Assignees = 'assignees',
  SortParam = 'sort',
  Page = 'page',
}

const MULTI_VAL_QUERY_PARAMS = [
  QueryParam.Projects,
  QueryParam.Statuses,
  QueryParam.Assignees,
];

type UrlState = {
  [QueryParam.Site]: string;
  [QueryParam.Projects]: string[];
  [QueryParam.Statuses]: IssueCategory[];
  [QueryParam.Search]: string;
  [QueryParam.Assignees]: string[];
  [QueryParam.SortParam]: string;
  [QueryParam.Page]: number;
};

const parseSort = (urlParams: UrlState): Sort => {
  return urlParams.sort[0] === '-'
    ? ({
        field: urlParams.sort.slice(1),
        order: SORT_ORDER.DESC,
      } as Sort)
    : ({
        field: urlParams.sort,
        order: SORT_ORDER.ASC,
      } as Sort);
};

const convertUrlParamsToRedux = (urlParams: UrlState): FilterState => ({
  siteCloudId: urlParams.site,
  projectIds: urlParams.projects,
  issueCategories: urlParams.statuses,
  textFilterQuery: urlParams.search,
  assignees: urlParams.assignees,
  sort: parseSort(urlParams),
  currentPage: urlParams.page,
});

const convertReduxToUrlParams = (reduxFilterState: FilterState): UrlState => ({
  site: reduxFilterState.siteCloudId,
  projects: reduxFilterState.projectIds,
  statuses: reduxFilterState.issueCategories,
  search: reduxFilterState.textFilterQuery,
  assignees: reduxFilterState.assignees,
  sort: reduxFilterState.sort
    ? reduxFilterState.sort.order === SORT_ORDER.ASC
      ? reduxFilterState.sort.field
      : `-${reduxFilterState.sort.field}`
    : '',
  page: reduxFilterState.currentPage,
});

const emptyUrlState: UrlState = convertReduxToUrlParams(emptyFilterState);

const getUrlFilterState = (location: Location) => {
  // @ts-ignore TODO: Fix ParsedQs to UrlState error here
  const params: UrlState = parse(location.search, { ignoreQueryPrefix: true });

  // Parse output could be "string[]" or "string" based on the number of values,
  // but we need it to be always "string[]".
  MULTI_VAL_QUERY_PARAMS.forEach(key => {
    if (params[key]) {
      // @ts-ignore
      params[key] = Array.isArray(params[key]) ? params[key] : [params[key]];
    }
  });

  // convert to number in order to match redux store type
  if (params.page) {
    params.page = Number(params.page);
  }

  return {
    ...emptyUrlState,
    ...params,
  };
};

const convertUrlStateToQueryString = (urlState: UrlState) =>
  stringify(urlState, {
    skipNulls: true,
    indices: false,
    filter: (_, value) => value || undefined,
  });

const usePrevious = <T>(value: T) => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const convertFilterStateToQueryString = (filterState: FilterState) =>
  convertUrlStateToQueryString(convertReduxToUrlParams(filterState));

export const useSyncFilterStateWithUrl = (
  location: Location,
  history: History
) => {
  const reduxFilterState = useSelector<BucketState, FilterState>(
    state => state.jira.jiraRepoPage.filterState
  );
  const prevRedux = usePrevious(reduxFilterState);

  const dispatch = useDispatch();

  // On the initial load of the page the first useEffect hook will execute and
  // potentially update the store. The second useEffect hook will then have a
  // a stale reference to the state of the store which causes an infinite loop.
  // Usage of useRef allows the state to be marked as stale.
  const hasReduxStoreBeenUpdated = useRef(false);
  hasReduxStoreBeenUpdated.current = false;

  // When the first time we update the URL to match the redux filter state,
  // we should do history.replace instead of history.push so that browser back
  // button action will continue to work as expected.
  // (Used "useRef" instead of "useState" to avoid the re-render when we update value)
  const hasEverUpdatedTheUrl = useRef(false);

  // This useEffect should run once in order to update the redux store with params
  // from the url on initial load, i.e. when a user bookmarked the page with
  // some filters active. If there are no query params present then attempt to
  // fetch and restore the previous state of filters from the users last session.
  useEffect(() => {
    const filterState = convertUrlParamsToRedux(getUrlFilterState(location));
    hasReduxStoreBeenUpdated.current = true;
    if (isEqual(filterState, emptyFilterState)) {
      dispatch(fetchJiraTabFiltersUserPreference());
    } else {
      dispatch(setJiraTabFiltersUserPreferenceNotRequired());
      dispatch(saveFilterState(filterState));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Besides the initial load, if the state of the URL and redux store are not equal then
  // one of them should be updated. If the redux store has changed, then update the URL to
  // match the redux store, otherwise update the redux store to match the URL. We also account
  // for the special cases of updating the URL query params to be empty and using 'replace'
  // for the first URL update.
  useEffect(() => {
    const urlFilterState = getUrlFilterState(location);
    const urlFormatReduxFilterState = convertReduxToUrlParams(reduxFilterState);
    const reduxHasBeenUpdated = !isEqual(reduxFilterState, prevRedux);
    const urlAndReduxAreEqual = isEqual(
      urlFilterState,
      urlFormatReduxFilterState
    );
    if (!hasReduxStoreBeenUpdated.current && !urlAndReduxAreEqual) {
      if (reduxHasBeenUpdated) {
        hasReduxStoreBeenUpdated.current = true;
        const qs = convertUrlStateToQueryString(urlFormatReduxFilterState);
        if (hasEverUpdatedTheUrl.current) {
          history.push(`${location.pathname}?${qs}`);
        } else {
          hasEverUpdatedTheUrl.current = true;
          history.replace(`${location.pathname}?${qs}`);
        }
      } else {
        // We need to consider the following cases when the URL gets updated:
        //
        // Case 1: Updated URL matches the empty filter state (ie. no query params).
        //  - Instead of leaving filters in an empty state we are going to
        //    redirect users to a URL that matches the current redux filter state.
        if (isEqual(urlFilterState, emptyUrlState)) {
          const qs = convertFilterStateToQueryString(reduxFilterState);
          history.replace(`${location.pathname}?${qs}`);
          return;
        }

        // Case 2: Updated URL state is not equal to the current redux filter state.
        //  - Update the redux filter state to match the URL.
        if (!isEqual(urlFilterState, reduxFilterState)) {
          dispatch(changeFilterState(convertUrlParamsToRedux(urlFilterState)));
        }
      }
    }
  });

  return null;
};
